import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, Home } from "lucide-react";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POLL_INTERVAL = 2000;
const MAX_POLLS = 15;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [amount, setAmount] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.amount) setAmount(data.amount);
        if (data.payment_status === "paid") {
          setStatus("success");
          return;
        }
        if (["failed", "expired", "refunded"].includes(data.payment_status)) {
          setStatus("failed");
          return;
        }
      } catch (e) {
        // keep polling on transient errors
      }
      attempts += 1;
      if (attempts >= MAX_POLLS) {
        setStatus("timeout");
        return;
      }
      setTimeout(poll, POLL_INTERVAL);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const dollars = amount ? (amount / 100).toFixed(2) : null;

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
      <div className="max-w-lg w-full bg-white rounded-2xl border border-slate-200 shadow-xl p-10 md:p-14 text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-14 h-14 text-[#0a3161] mx-auto animate-spin" />
            <h1 className="mt-6 font-serif-display text-3xl font-extrabold text-[#0a3161]">
              Confirming your donation...
            </h1>
            <p className="mt-3 text-slate-600">
              Please hold on while we verify your payment with Stripe. This usually takes just a few seconds.
            </p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="w-20 h-20 rounded-full bg-emerald-50 border-2 border-emerald-200 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h1 className="mt-6 font-serif-display text-3xl md:text-4xl font-extrabold text-[#0a3161]">
              Thank you{dollars ? ` for your $${dollars} contribution` : ""}!
            </h1>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Your support means the world to Greg and the campaign. A receipt has been sent to your email.
              Every dollar helps us reach more voters in the 19th District.
            </p>
            <p className="mt-3 font-script text-[#b22234] text-2xl">
              Hit It Out of the Park with Clark!
            </p>
          </>
        )}
        {status === "failed" && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-50 border-2 border-red-200 flex items-center justify-center mx-auto">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="mt-6 font-serif-display text-3xl font-extrabold text-[#0a3161]">
              Payment didn&rsquo;t go through
            </h1>
            <p className="mt-3 text-slate-600">
              We couldn&rsquo;t confirm your donation. If you were charged, it will be automatically refunded.
              Please try again or contact the campaign directly.
            </p>
          </>
        )}
        {status === "timeout" && (
          <>
            <Loader2 className="w-14 h-14 text-slate-400 mx-auto" />
            <h1 className="mt-6 font-serif-display text-3xl font-extrabold text-[#0a3161]">
              Still processing...
            </h1>
            <p className="mt-3 text-slate-600">
              Your payment is taking longer than usual to confirm. You&rsquo;ll receive an email
              confirmation once it&rsquo;s complete. No further action is required.
            </p>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="w-14 h-14 text-slate-400 mx-auto" />
            <h1 className="mt-6 font-serif-display text-3xl font-extrabold text-[#0a3161]">
              Missing session
            </h1>
            <p className="mt-3 text-slate-600">
              We couldn&rsquo;t find a payment session to check. Please return to the donation page.
            </p>
          </>
        )}
        <Link to="/">
          <Button className="mt-10 bg-[#0a3161] hover:bg-[#082448] text-white rounded-full h-12 px-8 font-oswald font-semibold uppercase tracking-[0.14em]">
            <Home className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default PaymentSuccess;
